import React, { Component } from 'react'
import { Link } from 'react-router-dom'
import { SplitContext } from '../../../../../containers/SplitContext'
import {
  buildBatchLink,
  isBarcodeToggleEnabled,
} from '../../../../../containers/App/fulfilmentPickingConfig'
import Table from '../../../../../components/Table'
import { Toggle } from '../../../../../components/Form'
import PacklistItemsView from './PacklistItemsView'
import PacklistTableHeader from './PacklistTableHeader'
import {
  getNestedState,
  updateStateRecursively,
} from '../../../../../lib/stateManagement'
import { getMessage } from '../../../../../lib/translator'
import { mapBarcodesFromPacklists } from './barcodes'
import { getPacklistStatusClassName } from './packlistStatus'
import './style.scss'

export const orderPacklistStatus = {
  PACKLIST_STATUS_UNSPECIFIED: 'Unspecified',
  PACKLIST_STATUS_PENDING: 'Pending',
  PACKLIST_STATUS_RECEIVED: 'Received',
  PACKLIST_STATUS_NOT_RECEIVED: 'Not Received',
  PACKLIST_STATUS_PICKING: 'Picking',
  PACKLIST_STATUS_PACKING: 'Packing',
  PACKLIST_STATUS_PACKED: 'Packed',
  PACKLIST_STATUS_CANCELLED: 'Cancelled',
}

export const tags = {
  all: 'Single Order',
  bulk: 'Bulky Items',
  general: 'Normal Items',
  ffpork: 'Pork Items',
  ampork: 'Ambient Pork Items',
  freshfrozen: 'FreshFrozen Items',
  others: 'Others',
  z2all: 'Single Order',
}

const PacklistRow = ({
  status,
  tag,
  picklistId,
  ownerName,
  ownerType,
  isDfOrder,
  isDonationOrder,
  isOperationsPage,
  packlistId,
  splits,
}) => {
  const getPacklistTitle = () => {
    if (tag === 'all' && ownerType && ownerName) {
      return ownerType === 'PACKLIST_OWNER_TYPE_VENDOR' ? ownerName : tags[tag]
    }
    return tags[tag]
  }

  const deliveryOptionLabel = isDonationOrder
    ? 'Donation'
    : isDfOrder
      ? 'Direct Fulfilment'
      : null

  return (
    <div className="sub-heading">
      <div>
        <strong> {getPacklistTitle()}</strong>
        {deliveryOptionLabel && (
          <span style={{ color: '#80959d' }}>
            &nbsp;({deliveryOptionLabel})
          </span>
        )}
        {isOperationsPage && picklistId && (
          <Link to={buildBatchLink(picklistId, splits)}>
            <u>Batch #{picklistId}</u>
          </Link>
        )}
        {packlistId && (
          <span className="packlist-id">
            &nbsp; (Packlist id: {packlistId})
          </span>
        )}
        <span style={{ float: 'right', color: '#80959d' }}>
          {' '}
          {getMessage('order.log.status')} :{' '}
          <span
            className={getPacklistStatusClassName(status)}
            data-testid="packlist-status"
          >
            {orderPacklistStatus[status]}
          </span>
        </span>
      </div>
    </div>
  )
}

const validatePacklist = (packlist) => {
  return packlist.items && packlist.items.length > 0
}

// Editing a quantity mutates the copy this component holds, so the copy has to be
// detached from the packlists the parent owns.
const clonePacklistData = (packlistData) =>
  JSON.parse(JSON.stringify(packlistData ? [...packlistData] : []))

class PacklistView extends Component {
  static contextType = SplitContext

  constructor(props) {
    super(props)
    this.state = this.generateStateFromProps(props, {
      packlistData: [],
    })
    // Barcodes are fetched lazily, only once the toggle is switched on, so pages
    // that do not need them cost no extra request.
    this.state.showBarcodes = false
    this.state.barcodesByItemId = {}
    this.state.barcodesLoading = false
    this.state.barcodesFailed = false
    this.getNestedState = getNestedState.bind(this)
    this.updateStateRecursively = updateStateRecursively.bind(this)
    this.onEditItemQuantity = this.onEditItemQuantity.bind(this)
    this.isOrderPendingOrCancelled = this.isOrderPendingOrCancelled.bind(this)
    this.isPacklistPendingOrCancelled =
      this.isPacklistPendingOrCancelled.bind(this)
    this.displayInvoiceSummary = this.displayInvoiceSummary.bind(this)
    this.toggleBarcodes = this.toggleBarcodes.bind(this)
  }

  componentDidUpdate(prevProps) {
    // State is a detached copy taken on mount, so without this the quantities typed
    // during an edit survive both a cancel and the refetch that follows a save.
    const editModeChanged = prevProps.itemEditMode !== this.props.itemEditMode
    // Refetched packlists are only adopted outside edit mode. Adopting them mid-edit
    // would clear the quantities on screen while the parent still holds them as
    // pending edits, so confirming would save a value the operator can no longer see.
    const packlistDataChanged =
      !this.props.itemEditMode &&
      prevProps.data?.packlistData !== this.props.data?.packlistData

    if (editModeChanged || packlistDataChanged) {
      const packlistData = clonePacklistData(this.props.data?.packlistData)
      this.setState({
        packlistData,
        // Barcodes are snapshotted on the packlist payload, so remapping them here
        // costs nothing and keeps the column in step with the rows now rendered.
        ...(this.state.showBarcodes
          ? { barcodesByItemId: mapBarcodesFromPacklists(packlistData) }
          : {}),
      })
    }
  }

  componentWillUnmount() {
    this.unmounted = true
  }

  toggleBarcodes(showBarcodes) {
    if (!showBarcodes) {
      this.setState({ showBarcodes: false })
      return
    }

    const alreadyLoaded = Object.keys(this.state.barcodesByItemId).length > 0
    if (alreadyLoaded) {
      this.setState({ showBarcodes: true })
      return
    }

    this.setState({
      showBarcodes: true,
      barcodesByItemId: mapBarcodesFromPacklists(this.state.packlistData),
      barcodesLoading: false,
      barcodesFailed: false,
    })
  }

  generateStateFromProps(props, state) {
    return Object.assign({}, JSON.parse(JSON.stringify(state)), {
      packlistData: clonePacklistData(props.data.packlistData),
    })
  }

  isOrderPendingOrCancelled(status) {
    return (
      status === 'DELIVERY_ORDER_STATUS_PENDING' ||
      status === 'DELIVERY_ORDER_STATUS_CANCELLED'
    )
  }

  isPacklistPendingOrCancelled(packlistStatus) {
    return (
      packlistStatus === 'PACKLIST_STATUS_PENDING' ||
      packlistStatus === 'PACKLIST_STATUS_CANCELLED'
    )
  }

  displayInvoiceSummary(itemEditMode) {
    return this.state.packlistData.length > 0 && !itemEditMode
  }

  itemOverPicked(item, editQuantity, isSoldByWeight) {
    // if it is weighted item, check with overpickingThreshold
    if (isSoldByWeight) {
      if (item.overPickingThreshold) {
        const overPickingThreshold = (
          item.orderedQuantity * item.overPickingThreshold
        ).toFixed(2)
        return editQuantity > overPickingThreshold
      }
    }
    return editQuantity > item.orderedQuantity
  }

  onEditItemQuantity(packlistIndex, itemIndex, editQuantity, isSoldByWeight) {
    let updatedQuantity = isSoldByWeight
      ? parseFloat(editQuantity || 0)
      : parseInt(editQuantity || 0, 10)
    const parts = updatedQuantity.toString().split('.')
    if (parts[1] && parts[1].length > 3) {
      parts[1] = parts[1].slice(0, 3)
      updatedQuantity = parts.join('.')
    }

    if (updatedQuantity >= 0) {
      const item = this.state.packlistData[packlistIndex].items[itemIndex]
      const overPicked = this.itemOverPicked(
        item,
        updatedQuantity,
        isSoldByWeight
      )

      this.setState((prevState) => {
        const packlistData = [...prevState.packlistData]
        if (!overPicked) {
          packlistData[packlistIndex].items[itemIndex].packedQuantity =
            updatedQuantity
        }
        return { packlistData }
      })
      this.props.updateItemQuantity(
        this.state.packlistData[packlistIndex].id,
        item,
        updatedQuantity,
        overPicked
      )
    }
  }

  renderPacklists(isPendingOrCancelled, itemEditMode, status) {
    const { splits } = this.context || {}
    return this.state.packlistData.map((packlistObj, index) => {
      const isPacklistPacked =
        packlistObj.status === 'PACKLIST_STATUS_PACKED' ||
        packlistObj.status === 'PACKLIST_STATUS_RECEIVED'
      return (
        <React.Fragment key={index}>
          {validatePacklist(packlistObj) && (
            <PacklistRow
              className="packlist-row"
              key={packlistObj.id}
              packlistId={packlistObj.id}
              status={packlistObj.status}
              tag={packlistObj.tag}
              picklistId={packlistObj.picklistId}
              ownerName={packlistObj.ownerName}
              ownerType={packlistObj.ownerType}
              isDfOrder={this.props.isDfOrder}
              isDonationOrder={this.props.isDonationOrder}
              isOperationsPage={this.props.isOperationsPage}
              splits={splits}
            />
          )}
          {packlistObj.items.map((itemObj, itemIndex) => (
            <React.Fragment key={`fragment-${itemObj.id}`}>
              <PacklistItemsView
                key={itemObj.itemId}
                item={itemObj}
                index={itemIndex}
                orderStatus={status}
                currency={this.props.currency}
                packlistStatus={packlistObj.status}
                isSubstitutedItem={!!itemObj.originalItem}
                isPendingOrCancelled={isPendingOrCancelled}
                isDsp={this.props.isDsp}
                canEditPacklistItems={itemEditMode && isPacklistPacked}
                packlistIndex={index}
                onEditItemQuantity={this.onEditItemQuantity}
                showBarcode={this.state.showBarcodes}
                barcode={this.state.barcodesByItemId[String(itemObj.itemId)]}
                barcodeLoading={this.state.barcodesLoading}
              />
              {itemObj && itemObj.originalItem && (
                <PacklistItemsView
                  key={itemObj.originalItem.itemId}
                  item={itemObj.originalItem}
                  index={itemIndex}
                  orderStatus={status}
                  currency={this.props.currency}
                  itemHasSubstitution={true}
                  isPendingOrCancelled={isPendingOrCancelled}
                  isDsp={this.props.isDsp}
                  showBarcode={this.state.showBarcodes}
                  barcode={
                    this.state.barcodesByItemId[
                      String(itemObj.originalItem.itemId)
                    ]
                  }
                  barcodeLoading={this.state.barcodesLoading}
                />
              )}
            </React.Fragment>
          ))}
        </React.Fragment>
      )
    })
  }

  render() {
    const productsInOrder = this.state.packlistData.flatMap(
      (x) => x.items
    ).length
    const { status, itemEditMode } = this.props
    const { splits } = this.context || {}
    const showBarcodeToggle = isBarcodeToggleEnabled(splits)

    const isPendingOrCancelled = this.isOrderPendingOrCancelled(status)

    return (
      <div className="packlist-items-view">
        <div className="flex-around section-title">
          <h3>
            {getMessage('order.details.itemsTable.title')}
            <span className="text-muted"> ({productsInOrder})</span>
          </h3>
          {showBarcodeToggle && (
            <div className="barcode-toggle" data-testid="barcode-toggle">
              <Toggle
                name="showBarcodes"
                className="show-barcodes"
                togglelabel={getMessage(
                  'order.details.itemsTable.header.barcode.show'
                )}
                value={this.state.showBarcodes}
                icons={false}
                onChange={this.toggleBarcodes}
              />
              {this.state.barcodesFailed && (
                <span className="barcode-toggle-error text-muted">
                  {getMessage('order.details.itemsTable.header.barcode.error')}
                </span>
              )}
            </div>
          )}
        </div>
        <Table>
          <PacklistTableHeader showBarcode={this.state.showBarcodes} />
          {this.renderPacklists(isPendingOrCancelled, itemEditMode, status)}
        </Table>
        {productsInOrder <= 0 && (
          <div className="empty-table-message">
            {getMessage('order.details.itemsTable.emptyMessage')}
          </div>
        )}
      </div>
    )
  }
}

PacklistView.defaultProps = {
  data: {
    items: [],
  },
  includeInvoice: true,
}

export default PacklistView
